/**
 * Admin Panel Token Manager
 * Handles all token storage and retrieval for admin authentication
 */

const TOKEN_PREFIX = 'admin_';
const TOKEN_KEY = `${TOKEN_PREFIX}authToken`;
const REFRESH_TOKEN_KEY = `${TOKEN_PREFIX}refreshToken`;
const USER_KEY = `${TOKEN_PREFIX}authUser`;

export const tokenManager = {
  /**
   * Store authentication tokens
   */
  setTokens: (accessToken, refreshToken = null, user = null) => {
    if (accessToken) {
      localStorage.setItem(TOKEN_KEY, accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
  },

  /**
   * Get access token
   */
  getAccessToken: () => {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Get refresh token
   */
  getRefreshToken: () => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  /**
   * Get stored user data
   */
  getUser: () => {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },

  /**
   * Update only the access token (for refresh token flow)
   */
  setAccessToken: (accessToken) => {
    if (accessToken) {
      localStorage.setItem(TOKEN_KEY, accessToken);
    }
  },

  /**
   * Update user data
   */
  setUser: (user) => {
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
  },

  /**
   * Check if tokens exist
   */
  hasTokens: () => {
    return !!localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Clear all tokens and user data (logout)
   */
  clearTokens: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  /**
   * Get authorization header
   */
  getAuthHeader: () => {
    const token = tokenManager.getAccessToken();
    if (token) {
      return {
        Authorization: `Bearer ${token}`,
      };
    }
    return {};
  },

  /**
   * Check if token is likely expired (basic check)
   */
  isTokenExpired: () => {
    const token = tokenManager.getAccessToken();
    if (!token) return true;

    try {
      // JWT format: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) return true;

      // Decode payload
      const payload = JSON.parse(atob(parts[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds

      return Date.now() > expirationTime;
    } catch (err) {
      // If we can't parse, assume expired
      return true;
    }
  },
};

export default tokenManager;
